import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from '@beautyapp/core';
import { CustomerCustomField } from '../models/customer-360.types';

/**
 * Thin wrapper around the `/users/:userId/custom-fields` endpoints
 * introduced by the `UserCustomFieldsModule`. All writes are
 * organization-scoped (a customer's hair formula / drink preference
 * travels across branches), which is why the drawer uses a single
 * bulk-save on close instead of per-row PUTs.
 */
@Injectable({ providedIn: 'root' })
export class CustomerCustomFieldsService {
  private api = inject(ApiService);

  list(userId: string): Observable<CustomerCustomField[]> {
    return this.api.get<CustomerCustomField[]>(`users/${userId}/custom-fields`);
  }

  upsert(
    userId: string,
    body: { fieldKey: string; fieldValue: string | null }
  ): Observable<CustomerCustomField> {
    return this.api.post<CustomerCustomField>(
      `users/${userId}/custom-fields`,
      body
    );
  }

  update(
    userId: string,
    id: number,
    body: { fieldKey?: string; fieldValue?: string | null }
  ): Observable<CustomerCustomField> {
    return this.api.put<CustomerCustomField>(
      `users/${userId}/custom-fields/${id}`,
      body
    );
  }

  remove(userId: string, id: number): Observable<void> {
    return this.api.delete<void>(`users/${userId}/custom-fields/${id}`);
  }

  bulkReplace(
    userId: string,
    fields: { fieldKey: string; fieldValue: string | null }[]
  ): Observable<CustomerCustomField[]> {
    // Backend binds BulkUpsertUserCustomFieldsDto — the array must be
    // wrapped under `fields`, otherwise dto.fields is undefined server-side
    // and the save is silently dropped (and existing fields get wiped).
    return this.api.put<CustomerCustomField[]>(
      `users/${userId}/custom-fields/bulk`,
      { fields }
    );
  }
}
